import React, { useId, useState } from 'react';
import { Info } from '@phosphor-icons/react';

/**
 * Small "i" affordance next to a chart/metric title. On hover or focus it shows
 * a short, plain-language explanation of what the reader is looking at — aimed at
 * a non-expert, not a methodology restatement. Theme-aware, token-driven, a11y
 * (keyboard focusable + aria-describedby). Click also toggles it (touch/keyboard).
 */
export interface InfoHintProps {
  /** Plain-language explanation shown in the popover. */
  text: React.ReactNode;
  /** Accessible label for the trigger button. */
  ariaLabel?: string;
  size?: number;
  className?: string;
  /** Horizontal anchor of the popover relative to the icon. */
  align?: 'center' | 'left' | 'right';
}

const InfoHint: React.FC<InfoHintProps> = ({
  text, ariaLabel = 'Más información', size = 14, className = '', align = 'center',
}) => {
  const [open, setOpen] = useState(false);
  const id = useId();

  const pos =
    align === 'left' ? 'left-0' :
    align === 'right' ? 'right-0' :
    'left-1/2 -translate-x-1/2';

  return (
    <span className={`relative inline-flex items-center ${className}`}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
        className="inline-flex items-center justify-center rounded-full text-[var(--text-tertiary)] hover:text-[var(--aipron-accent)] transition-colors align-middle"
        style={{ cursor: 'help' }}
      >
        <Info size={size} weight="bold" />
      </button>
      {open && (
        <span
          role="tooltip"
          id={id}
          className={`absolute z-50 bottom-full ${pos} mb-2 w-64 max-w-[80vw] p-3 rounded-lg text-xs font-normal normal-case tracking-normal leading-relaxed shadow-xl pointer-events-none`}
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

export default InfoHint;
